import type { MatterClass, MatterClassInput } from '@/api/specification'

/**
 * The editable form of a specification, and the rules that govern it.
 *
 * Its own module beside `SpecificationEditor.tsx` for the reason `casePath.ts`
 * is one beside `CaseScreen.tsx`: these are decisions about the data, they are
 * the part worth testing directly, and a test that has to render a form to ask
 * "does an edited proposal still count as sourced" is a test of the wrong
 * thing.
 *
 * ## The rule that is not a preference
 *
 * **`nice_source_id` is never inferred and never survives an edit.** A row is
 * "sourced" when its proposed wording *is* a pre-approved description,
 * verbatim — so the draft carries the description it was picked from, and the
 * moment the text stops matching, the id goes with it ({@link withProposal}).
 * Leaving the id on hand-edited wording would put a green "from pre-approved
 * wording" badge on a sentence nobody can quote, which is precisely the claim
 * the gate's `SPEC_WORDING_UNANCHORED` exists to catch. The gate is the second
 * opinion; this is the first, and they must not disagree.
 *
 * Two smaller rules follow the data layer's own (`api/specification.ts`): empty
 * proposed wording saves as `null` and never `''`, because "nobody has amended
 * this class" and "this class is amended to nothing" are different answers and
 * only one of them is a filing; and the write is the whole set, because an
 * identification of goods and services is one document.
 */

export interface DraftRow {
  /**
   * A stable React key. Not the class number: that is the field being edited,
   * so keying on it remounts the inputs mid-keystroke and loses focus.
   */
  key: string
  /**
   * The class as typed, so a half-entered "1" on the way to "12" is
   * representable. Coerced once, at save.
   */
  classNumber: string
  currentText: string
  proposedText: string
  /**
   * The pre-approved description this proposal was taken from, verbatim, or
   * `null`. Held so that an edit can be detected — see the module docstring.
   */
  sourceText: string | null
  niceSourceId: number | null
}

let nextKey = 0

/** A key no other row holds. Module-scoped, so remounting cannot collide. */
export function freshKey(): string {
  nextKey += 1
  return `row-${nextKey}`
}

export function emptyRow(): DraftRow {
  return {
    key: freshKey(),
    classNumber: '',
    currentText: '',
    proposedText: '',
    sourceText: null,
    niceSourceId: null,
  }
}

/**
 * Seed the editor from what the server holds.
 *
 * `sourceText` comes from `proposed_text` on a sourced row, which is exact
 * rather than approximate: sourced *means* the proposal is the description
 * verbatim, so an untouched row survives the round trip still sourced.
 */
export function toDraft(rows: MatterClass[]): DraftRow[] {
  return rows.map((row) => ({
    key: freshKey(),
    classNumber: String(row.class_number),
    currentText: row.current_text,
    proposedText: row.proposed_text ?? '',
    sourceText: row.nice_source_id === null ? null : (row.proposed_text ?? null),
    niceSourceId: row.nice_source_id,
  }))
}

/** A proposal as typed — which, unless it still matches, is no longer sourced. */
export function withProposal(row: DraftRow, proposed: string): DraftRow {
  const stillSourced = row.sourceText !== null && proposed.trim() === row.sourceText.trim()
  return {
    ...row,
    proposedText: proposed,
    sourceText: stillSourced ? row.sourceText : null,
    niceSourceId: stillSourced ? row.niceSourceId : null,
  }
}

/** A proposal taken from a retrieved description, which carries its id. */
export function withSourcedProposal(row: DraftRow, description: string, id: number): DraftRow {
  return { ...row, proposedText: description, sourceText: description, niceSourceId: id }
}

export function toInput(draft: DraftRow[]): MatterClassInput[] {
  return draft.map((row) => {
    const proposed = row.proposedText.trim()
    return {
      class_number: Number(row.classNumber),
      current_text: row.currentText.trim(),
      // `null`, not `''`. The column is nullable for this reason.
      proposed_text: proposed === '' ? null : proposed,
      nice_source_id: row.niceSourceId,
    }
  })
}

/** The message shown against a row whose class number is unusable. */
export const CLASS_RANGE_ERROR = 'The Nice Classification runs from 1 to 45.'
/** The message shown against a row with no current wording. */
export const CURRENT_TEXT_ERROR =
  'What the application claims today is the one thing this row needs.'

/**
 * What is wrong with the draft, by row key, plus a `form` entry for anything
 * wrong with the set rather than with a row.
 *
 * Checked here rather than left to the server's 422 because every one of these
 * is visible on the screen in front of the attorney, and a round trip to be
 * told so is a round trip that teaches nothing.
 */
export function draftErrors(draft: DraftRow[]): Record<string, string> {
  const errors: Record<string, string> = {}
  const seen = new Map<number, number>()

  draft.forEach((row) => {
    const parsed = Number(row.classNumber)
    if (row.classNumber.trim() === '' || !Number.isInteger(parsed) || parsed < 1 || parsed > 45) {
      errors[row.key] = CLASS_RANGE_ERROR
      return
    }
    if (row.currentText.trim() === '') {
      errors[row.key] = CURRENT_TEXT_ERROR
      return
    }
    // Counted only for rows that are otherwise sound, so a row already flagged
    // for its number does not also produce a duplicate complaint about it.
    seen.set(parsed, (seen.get(parsed) ?? 0) + 1)
  })

  const repeated = [...seen.entries()].filter(([, count]) => count > 1).map(([number]) => number)
  if (repeated.length > 0) {
    errors.form =
      `Class ${repeated.join(', ')} appears twice. An application claims one identification ` +
      'per class, and a response can only file one.'
  }
  if (draft.length === 0) {
    errors.form = 'Nothing to save. Add a class, or cancel to leave the specification as it is.'
  }
  return errors
}
